<template>
  <!-- HEADER -->
  <div class="header">
    <div class="container-fluid">
      <!-- Table -->
      <div class="card">
        <div class="card-header">
          <!-- Title -->
          <h4 class="card-header-title">LSP</h4>
          <!-- Add Button -->
          <div class="card-header-action">
            <b-button
              class="btn-pill"
              variant="danger"
              @click="addData()">
              <span class="fe fe-file-plus"></span>
              Add LSP
            </b-button>
          </div>
        </div>
        <!-- Filter -->
        <div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-4 offset-6">
                <div class="form-group">
                  <input
                    type="text"
                    v-model="filters.search"
                    class="form-control"
                    placeholder="Search Here..."
                    @input="onSearch" />
                </div>
              </div>
              <div class="col-md-2">
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  @click="clearFilters">
                  <span class="fe fe-x"></span> Clear Filter
                </button>
              </div>
            </div>
          </div>
        </div>
        <vue-good-table
          :columns="columns"
          :rows="lspData"
          :sort-options="{
            enabled: true
          }"
          style-class="vgt-table border-0 table">
          <!-- Loading -->
          <div slot="emptystate" class="text-center font-size-14">
            {{ isLoading ? 'Loading...' : 'No data available' }}
          </div>

          <!-- Custom Rows -->
          <template slot="table-row" slot-scope="props">
            <!-- No Column -->
            <span v-if="props.column.field == 'no'">
              {{ (body.page - 1) * body.limit + props.index + 1 }}
            </span>

            <!-- Table Action -->
            <span v-else-if="props.column.field == 'action'">
              <b-button
                class="no-wrap btn-pill me-2"
                variant="info"
                size="sm"
                @click="viewData(props.row)">
                <span class="fe fe-eye"></span>
              </b-button>
              <b-button
                class="no-wrap btn-pill me-2"
                variant="warning"
                size="sm"
                @click="updateData(props.row)">
                <span class="fe fe-edit"></span>
              </b-button>
              <b-button
                class="no-wrap btn-pill"
                variant="danger"
                size="sm"
                @click="deleteData(props.row.id)">
                <span class="fe fe-trash"></span>
              </b-button>
            </span>

            <!-- Default -->
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>

        <!-- Custom Pagination -->
        <div class="card-footer border-top">
          <div class="row align-items-center">
            <div class="col-md-4">
              <div class="d-flex align-items-center">
                <span class="text-muted small me-2">Show</span>
                <select 
                  v-model="body.limit" 
                  class="form-select form-select-sm" 
                  style="width: 70px;"
                  @change="onPerPageChange">
                  <option v-for="option in perPageOptions" :key="option" :value="option">{{ option }}</option>
                </select>
                <span class="text-muted small ms-2">entries</span>
              </div>
            </div>
            <div class="col-md-4 text-center">
              <small class="text-muted">
                Showing {{ showingFrom }} to {{ showingTo }} of {{ totalItems }} entries
              </small>
            </div>
            <div class="col-md-4">
              <nav>
                <ul class="pagination pagination-sm justify-content-end mb-0">
                  <li class="page-item" :class="{ disabled: body.page === 1 }">
                    <button class="page-link" @click="changePage(body.page - 1)" :disabled="body.page === 1">
                      <i class="fe fe-chevron-left"></i>
                    </button>
                  </li>
                  <li 
                    v-for="(page, index) in visiblePages" 
                    :key="index"
                    class="page-item" 
                    :class="{ active: body.page === page, disabled: page === '...' }">
                    <button 
                      v-if="page === '...'" 
                      class="page-link" 
                      disabled>
                      ...
                    </button>
                    <button 
                      v-else
                      class="page-link" 
                      @click="changePage(page)">
                      {{ page }}
                    </button>
                  </li>
                  <li class="page-item" :class="{ disabled: body.page === totalPages }">
                    <button class="page-link" @click="changePage(body.page + 1)" :disabled="body.page === totalPages">
                      <i class="fe fe-chevron-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal -->
      <b-modal 
        id="modal-lsp" 
        centered 
        hide-footer 
        :title="modalTitle"
        size="lg">
        <!-- Add Form -->
        <form @submit.prevent="saveAdd()" v-if="isAdd === true">
          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <label>LSP Name <span class="text-danger">*</span></label>
                <input
                  v-validate="'required'"
                  name="LSP Name"
                  type="text"
                  v-model="bodyAdd.name"
                  class="form-control"
                  placeholder="LSP Name" />
                <span
                  class="text-sm text-danger"
                  v-show="errors.has('LSP Name')"
                  >{{ errors.first("LSP Name") }}</span>
              </div>
            </div>
            <div class="col-md-12">
              <div class="form-group">
                <label>Contact Person <span class="text-danger">*</span></label>
                <input
                  v-validate="'required'"
                  name="Contact Person"
                  type="text"
                  v-model="bodyAdd.contact_person"
                  class="form-control"
                  placeholder="Contact Person" />
                <span
                  class="text-sm text-danger"
                  v-show="errors.has('Contact Person')"
                  >{{ errors.first("Contact Person") }}</span>
              </div>
            </div>
          </div>
          <div class="form-group text-right">
            <button
              type="button"
              class="btn btn-secondary me-2"
              @click="$bvModal.hide('modal-lsp')">
              Cancel
            </button>
            <button
              class="btn btn-danger"
              type="submit"
              :disabled="isLoading">
              <span
                v-if="isLoading"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"></span>
              Save
            </button>
          </div>
        </form>

        <!-- Edit Form -->
        <form @submit.prevent="saveUpdate()" v-else-if="isAdd === false">
          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <label>LSP Name <span class="text-danger">*</span></label>
                <input
                  v-validate="'required'"
                  name="LSP Name"
                  type="text"
                  v-model="bodyUpdate.name"
                  class="form-control"
                  placeholder="LSP Name" />
                <span
                  class="text-sm text-danger"
                  v-show="errors.has('LSP Name')"
                  >{{ errors.first("LSP Name") }}</span>
              </div>
            </div>
            <div class="col-md-12">
              <div class="form-group">
                <label>Contact Person <span class="text-danger">*</span></label>
                <input
                  v-validate="'required'"
                  name="Contact Person"
                  type="text"
                  v-model="bodyUpdate.contact_person"
                  class="form-control"
                  placeholder="Contact Person" />
                <span
                  class="text-sm text-danger"
                  v-show="errors.has('Contact Person')"
                  >{{ errors.first("Contact Person") }}</span>
              </div>
            </div>
          </div>
          <div class="form-group text-right">
            <button
              type="button"
              class="btn btn-secondary me-2"
              @click="$bvModal.hide('modal-lsp')">
              Cancel
            </button>
            <button
              class="btn btn-danger"
              type="submit"
              :disabled="isLoading">
              <span
                v-if="isLoading"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"></span>
              Update
            </button>
          </div>
        </form>

        <!-- View Mode -->
        <div v-else>
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label><strong>LSP Name:</strong></label>
                <p>{{ viewDetailData.name }}</p>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label><strong>Contact Person:</strong></label>
                <p>{{ viewDetailData.contact_person }}</p>
              </div>
            </div>
          </div>
          <div class="form-group text-right">
            <button
              type="button"
              class="btn btn-secondary"
              @click="$bvModal.hide('modal-lsp')">
              Close
            </button>
          </div>
        </div>
      </b-modal>
    </div>
  </div>
</template>
<script>
export default {
  name: "Lsp",
  computed: {
    lspData() {
      return this.$store.getters["lsp/getLsps"] || [];
    },
    totalItems() {
      return this.$store.getters["lsp/getTotalItems"] || 0;
    },
    totalPages() {
      return this.$store.getters["lsp/getTotalPages"] || 1;
    },
    isLoading() {
      return this.$store.getters["lsp/getLoading"];
    },
    // Pagination computed
    showingFrom() {
      if (this.totalItems === 0) return 0;
      return (this.body.page - 1) * this.body.limit + 1;
    },
    showingTo() {
      const to = this.body.page * this.body.limit;
      return to > this.totalItems ? this.totalItems : to;
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.body.page;
      
      // Always show at least page 1
      if (total <= 1) {
        pages.push(1);
      } else if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        
        if (current > 3) {
          pages.push('...');
        }
        
        const start = Math.max(2, current - 1);
        const end = Math.min(total - 1, current + 1);
        
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
        
        if (current < total - 2) {
          pages.push('...');
        }
        
        pages.push(total);
      }
      
      return pages;
    }
  },
  data() {
    return {
      listTabMenu: {
        privilages: {
          create: true,
          read: true,
          update: true,
          delete: true
        }
      },
      body: {
        page: 1,
        limit: 10,
        search: "",
        order: ""
      },
      perPageOptions: [10, 25, 50, 100],
      filters: {
        start_date: "",
        end_date: "",
        search: ""
      },
      bodyAdd: {
        name: "",
        contact_person: ""
      },
      bodyUpdate: {
        id: "",
        name: "",
        contact_person: ""
      },
      viewDetailData: {},
      isAdd: true,
      modalTitle: "Add LSP",
      searchTimeout: null,
      columns: [
        {
          label: "NO",
          field: "no",
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary align-middle",
          width: "5%",
          sortable: false
        },
        {
          label: "LSP NAME",
          field: "name",
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary align-middle",
          width: "45%"
        },
        {
          label: "CONTACT PERSON",
          field: "contact_person",
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary align-middle",
          width: "35%"
        },
        {
          label: "ACTION",
          field: "action",
          thClass: "table-header no-wrap text-muted font-weight-bold",
          tdClass: "no-wrap color-secondary align-middle",
          width: "15%",
          sortable: false
        }
      ]
    };
  },
  mounted() {
    // this.setDummyData(); // Temporary dummy data
    this.getLsps(); // Uncomment when API is ready
    this.setMenuPermissions();
  },
  methods: {
    // Set dummy data for testing
    setDummyData() {
      // Simulate API response structure
      const dummyData = [
        {
          id: 1,
          name: "LSP DARU SATU (LSP PIM)",
          contact_person: "082251607500",
        },
        {
          id: 2,
          name: "LSP Pertambangan Indonesia Mandiri (LSP PIM)",
          contact_person: "082251607500",
        }
      ];
      
      // Commit dummy data to store
      this.$store.commit("lsp/setLsps", {
        data: dummyData,
        totalItems: dummyData.length
      });
    },

    // Set menu permissions
    setMenuPermissions() {
      const listMenu = JSON.parse(localStorage.getItem('listMenu') || '[]');
      const currentPath = window.location.pathname;
      const menu = listMenu.find((menu) =>
        menu.link === String(currentPath) || menu.children.some((child) => child.link === String(currentPath))
      );
      this.listTabMenu = menu ? menu.children.find((item) => item.link === currentPath) || menu : {};
    },

    // Search with debounce
    onSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.body.search = this.filters.search;
        this.body.page = 1;
        this.getLsps();
      }, 500);
    },

    // Clear all filters
    clearFilters() {
      this.filters = {
        start_date: "",
        end_date: "",
        search: ""
      };
      this.body.search = "";
      this.body.page = 1;
      this.getLsps();
    },

    // Actions
    addData() {
      this.isAdd = true;
      this.modalTitle = "Add LSP";
      this.resetAddForm();
      this.$bvModal.show("modal-lsp");
    },

    viewData(data) {
      this.isAdd = null; // View mode
      this.modalTitle = "View LSP";
      
      // Get detailed LSP data by ID
      this.$store.dispatch("lsp/getLspById", data.id)
        .then((response) => {
          this.viewDetailData = response.data;
          this.$bvModal.show("modal-lsp");
        })
        .catch((error) => {
          console.error("Error fetching LSP details:", error);
          // Fallback to using the data passed from table
          this.viewDetailData = { ...data };
          this.$bvModal.show("modal-lsp");
        });
    },

    updateData(data) {
      console.log(data)
      this.isAdd = false;
      this.modalTitle = "Edit LSP";
      this.bodyUpdate.id = data.id;
      this.bodyUpdate.name = data.name;
      this.bodyUpdate.contact_person = data.contact_person;
      this.$bvModal.show("modal-lsp");
    },

    resetAddForm() {
      this.bodyAdd = {
        name: "",
        contact_person: ""
      };
      this.$validator.reset();
    },

    // Save methods
    saveAdd() {
      this.$validator.validateAll().then(async (result) => {
        if (!result) return false;
        
        try {
          await this.$store.dispatch("lsp/createLsp", {
            name: this.bodyAdd.name,
            contact_person: this.bodyAdd.contact_person,
          });
          this.$swal({
            title: "Success",
            text: "LSP berhasil ditambahkan",
            icon: "success",
            confirmButtonText: "OK"
          }).then(() => {
            this.getLsps();
            this.$bvModal.hide("modal-lsp");
            this.resetAddForm();
          });
        } catch (error) {
          console.log("Error response:", error);
          let errorMessage = "Error while adding competency";
          
          // Handle API error response
          if (error.response && error.response.data) {
            const errorData = error.response.data;
            if (errorData.httpCode === 400 && errorData.error) {
              errorMessage = errorData.error;
            } else if (errorData.message) {
              errorMessage = errorData.message;
            } else if (errorData.httpMessage) {
              errorMessage = errorData.httpMessage;
            }
          }
          
          this.$swal("Error", errorMessage, "error");
        }
      });
    },

    saveUpdate() {
      this.$validator.validateAll().then(async (result) => {
        if (!result) return false;
        
        try {
          await this.$store.dispatch("lsp/updateLsp", {
            id: this.bodyUpdate.id,
            data: {
              name: this.bodyUpdate.name,
              contact_person: this.bodyUpdate.contact_person
            }
          });
          this.$swal({
            title: "Success",
            text: "LSP berhasil diperbarui",
            icon: "success",
            confirmButtonText: "OK"
          }).then(() => {
            this.getLsps();
            this.$bvModal.hide("modal-lsp");
          });
        } catch (error) {
          console.log("Error response:", error);
          let errorMessage = "Error while updating competency";
          
          // Handle API error response
          if (error.response && error.response.data) {
            const errorData = error.response.data;
            if (errorData.httpCode === 400 && errorData.error) {
              errorMessage = errorData.error;
            } else if (errorData.message) {
              errorMessage = errorData.message;
            } else if (errorData.httpMessage) {
              errorMessage = errorData.httpMessage;
            }
          }
          
          this.$swal("Error", errorMessage, "error");
        }
      });
    },

    deleteData(id) {
      this.$swal({
        title: "Apakah Anda yakin?",
        text: "Tindakan ini tidak dapat dibatalkan!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#00D97E",
        cancelButtonColor: "#E63757",
        confirmButtonText: "Ya, hapus",
        cancelButtonText: "Batal"
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await this.$store.dispatch("lsp/deleteLsp", id);
            this.getLsps();
            this.$swal("Terhapus", "Competency telah dihapus", "success");
          } catch (error) {
            this.$swal("Error", "Gagal menghapus LSP", "error");
          }
        }
      });
    },

    // API calls
    getLsps() {
      const params = { ...this.body };
      if (this.filters.start_date) params.start_date = this.filters.start_date;
      if (this.filters.end_date) params.end_date = this.filters.end_date;
      
      this.$store.dispatch("lsp/getLsps", params);
    },

    // Pagination
    onPerPageChange() {
      this.body.page = 1;
      this.getLsps();
    },

    changePage(page) {
      if (page === '...' || page < 1 || page > this.totalPages) return;
      this.body.page = page;
      this.getLsps();
    }
  }
};
</script>

<style scoped>
.me-2 {
  margin-right: 0.5rem;
}

.ms-2 {
  margin-left: 0.5rem;
}

/* Pagination */
.pagination .page-link {
  color: #2c7be5;
  border: 1px solid #dee2e6;
  padding: 0.375rem 0.75rem;
  border-radius: 0.25rem;
  min-width: 36px;
  text-align: center;
}

.pagination .page-item.active .page-link {
  background-color: #e63757;
  border-color: #e63757;
  color: white;
}

.pagination .page-item.disabled .page-link {
  cursor: not-allowed;
  opacity: 0.5;
  background-color: transparent;
}

.pagination .page-link:hover:not(.disabled) {
  background-color: #f8f9fa;
}
</style>
